import React, { useMemo } from "react";
import { Apartment } from "@/models/responses";
import Grid, { GridButton } from "@/lib/organisms/Grid";
import { useAppData } from "@/hooks/useAppData";
import { useNavigate } from "react-router-dom";
import { useUserSettingsDispatch } from "@/store/slices/userSettingsDispatch";
import { PlusIcon } from "lucide-react";
import { useAllApartments } from "@/store/slices/apartments";
import { useApartmentFilters } from "@/hooks/useApartmentFilters";
import ApartmentFilterBar from "@/lib/components/apartment-filter-bar";
import { buildApartmentColumnDefinitions, mapApartmentToRow, decommissionedRowClass } from "./apartments.utils";

export interface ApartmentRow {
    id: number;
    name: string;
    shortName: string;
    liveSince: Date;
    dateRemoved: Date | null;
    owners: string[];
    group: string;
    nBeds: number;
    squareMeters: number;
    energyCertificate: Date | null;
    initialTaxisRentalAgreement: string;
    latestTaxisRentalAgreement: string;
    internetCompany: string;
    internetLine: string;
    internetUsername: string;
    internetPassword: string;
    atak: string;
    shortTermRegistryId: string;
    electricityCompany: string;
    electricityCertificateTerminationDate: Date | null;
    electricityRf: string;
    electricitySupplyId: string;
    eydapSupplyId: string;
    eydapRegistryId: string;
    address: string;
    imageUrl: string;
    disinfectionDate: Date | null;
    disinfectionCertificateNumber: string;
    fireExtinguisherRefillCertificateDate: Date | null;
    floor: number;
    insuranceCompany: string;
    insuranceContractId: string;
    airbnbUrl: string | null;
    bookingUrl: string | null;
}

export function ApartmentsPage(): React.ReactElement {
    const { ownerIdMap, groupIdMap, groupOptions, selectedGroup } = useAppData();
    const { setSelectedGroup } = useUserSettingsDispatch();
    const apartmentDetails: Apartment[] = useAllApartments();
    const { predicates } = useApartmentFilters();
    const navigate = useNavigate();

    const columnDefinitions = useMemo(() => buildApartmentColumnDefinitions(), []);

    const apartmentRows = useMemo<ApartmentRow[]>(() => {
        return apartmentDetails
            .filter((apartment) => (selectedGroup === 0 ? true : selectedGroup === apartment.groupId))
            .filter((apartment) => predicates.every((predicate) => predicate(apartment)))
            .map((apartment) =>
                mapApartmentToRow(
                    apartment,
                    (ownerId) => ownerIdMap.get(ownerId)?.fullName || "",
                    (groupId) => groupIdMap.get(groupId)?.name || "",
                ),
            );
    }, [apartmentDetails, ownerIdMap, groupIdMap, selectedGroup, predicates]);

    // The Grid renders `apartmentRows`, which is already the filtered (active)
    // set when the Show-decommissioned toggle is OFF — so any count derived from
    // it reflects only what the grid shows (AS-6, not overstated). `today` is
    // captured once per render for the muted-row predicate.
    const today = new Date();

    const dropdownFilters = [
        {
            label: "Group",
            options: groupOptions,
            selectedOption: selectedGroup.toString(),
            onSelectionChanged: (group: string) => setSelectedGroup(Number(group)),
        },
    ];

    const addNewApartment: GridButton = {
        label: "Add Apartment",
        onClick: () => navigate("/admin/apartments/new"),
        icon: PlusIcon,
    };

    return (
        <div className="flex flex-col space-y-6 p-2 py-0" data-testid="apartments-page">
            <ApartmentFilterBar />
            <Grid<ApartmentRow>
                columnDefs={columnDefinitions}
                gridName={"Apartments"}
                getRowId={(row) => row.data.id.toString()}
                initialRowData={apartmentRows}
                canExport={true}
                buttons={[addNewApartment]}
                dropdownFilters={dropdownFilters}
                getRowClass={(params) => decommissionedRowClass(params, today)}
            />
        </div>
    );
}
